import { useContext } from "react";
import { endoso } from "services";
import { EndosoContext } from "pages/endoso/context";
import {
  ACRONIMO_CP_RECEPTOR,
  ACRONIMO_REGIMEN_FISCAL,
  isDatosFiscalesCompleto,
} from "shared/utils/endosoTypes";

/* Texto regulatorio del falso timbrado (HU6): no modificar. */
const MENSAJE_TIMBRADO_INVALIDO =
  "Validar información ingresada contra su Constancia de Situación";

/**
 * Resuelve la descripción del régimen desde el catálogo que llegó en
 * datosEndoso: dataSend solo guarda el id y el contrato exige mandar ambos.
 * La comparación es case-insensitive porque handleChangeData (formsSection)
 * pasa los valores de texto a mayúsculas.
 */
const getDescripcionRegimen = (datosEndoso, idRegimenFiscal) =>
  datosEndoso
    ?.find((dato) => dato.acronimo === ACRONIMO_REGIMEN_FISCAL)
    ?.catalogo?.catalogo?.find(
      (option) =>
        String(option.id).toUpperCase() ===
        String(idRegimenFiscal).toUpperCase()
    )?.descripcion ?? null;

/**
 * Nombre completo del contratante para el body de guarda-datos-fiscales.
 * Misma composición que `rulesBy-1-2-3` (moral → razón social; física →
 * nombres + apellidos), pero filtrando vacíos: el segundo nombre es opcional y
 * concatenarlo a ciegas deja un espacio doble en el nombre que se timbra.
 */
const getNombreContratante = (dataSend = {}) =>
  (dataSend.TIPPER === "2"
    ? dataSend.NOMBRE ?? ""
    : [dataSend.NOMBR1, dataSend.NOMBR2, dataSend.DSAPEL1, dataSend.DSAPEL2]
        .filter(Boolean)
        .join(" ")
  ).trim();

export const useRulesBy25 = () => {
  const { formDta, createMessage, setShowAlcanceRFC, setTotalRecibos } =
    useContext(EndosoContext);

  const handleGuardarBorrador = async (dataSend = {}) => {
    if (!isDatosFiscalesCompleto(dataSend)) {
      createMessage({
        text: "DEBE CAPTURAR EL CP DEL RECEPTOR (5 DIGITOS) Y EL ID REGIMEN FISCAL",
        variant: "warning",
      });
      return;
    }

    const idRegimenFiscal = dataSend[ACRONIMO_REGIMEN_FISCAL];
    const body = {
      solicitud: formDta.noSolicitud,
      nivel: 1, // valor fijo por contrato
      codigoPostal: String(dataSend[ACRONIMO_CP_RECEPTOR]),
      idRegimenFiscal: String(idRegimenFiscal),
      /* rfc y nombre: datos del contratante que el formulario muestra
       * bloqueados. Salen de dataSend, igual que el resto del body. */
      rfc: String(dataSend.RFC ?? "").trim(),
      nombre: getNombreContratante(dataSend),
      descripcionRegimenFiscal: getDescripcionRegimen(
        formDta.datosEndoso,
        idRegimenFiscal
      ),
    };

    const response = await endoso.guardaDatosFiscales(body);

    /* El falso timbrado pasó: se ofrece aplicar el endoso a todo el RFC. */
    if (response?.data?.codigo === 1) {
      setTotalRecibos(response.data.total ?? 0);
      setShowAlcanceRFC(true);
      return;
    }

    /* Sin data no hubo respuesta del backend (red / HTTP >= 300). */
    createMessage({
      text: response?.data
        ? MENSAJE_TIMBRADO_INVALIDO
        : "OCURRIO UN PROBLEMA CON SU SOLICITUD",
      variant: "warning",
    });
  };

  return { handleGuardarBorrador };
};
